import React, { useState } from 'react'

import { useMediaQuery, useTheme } from '@mui/material'

import {
  DashboardFilter,
  ParsedDashboardFilterOptions
} from 'types/GlobalDashboardFilter'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { Action, Context } from 'types/GlobalUserEvents'
import type { TSavedDashboardFilterDto } from '@homepal/api-contract'

import { useSavedDashboardFilterActions } from 'redux/actions/SavedDashboardFilters'

import Button from 'components_new/atoms/Button'

import SplitButton from 'components_new/molecules/SplitButton'

import SaveUserFilterDialog from '../dialogs/SaveUserFilterDialog'
import { formatPutConditions } from './utils'
import { useTranslation } from 'language/translator'
import { isPublicDashboard, TDashboard } from 'types/GlobalDashboard'

interface ActionsProps {
  dashboard: TDashboard
  dashboardFilter: DashboardFilter
  dashboardFilterOptions: Map<string, ParsedDashboardFilterOptions>
  savedDashboardFilters: TSavedDashboardFilterDto[]
  setDashboardFilter: (value: DashboardFilter) => void
  setSavedDashboardFilter: (
    value: TSavedDashboardFilterDto,
    skipFetch?: boolean
  ) => void
  setTab: (tab: 'current' | 'saved') => void
}

const Actions = (props: ActionsProps) => {
  const {
    dashboard,
    dashboardFilter,
    dashboardFilterOptions,
    savedDashboardFilters,
    setDashboardFilter,
    setSavedDashboardFilter,
    setTab
  } = props

  const { tryCreateSavedDashboardFilter, tryUpdateSavedDashboardFilter } =
    useSavedDashboardFilterActions()

  const isPublic = isPublicDashboard(dashboard)
  const { t } = useTranslation()

  const [dialog, setDialog] = useState<'save' | false>(false)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'), {
    noSsr: true
  })

  const handleSave = (title: string) => {
    if (isPublic) return

    UserEvents.log({
      action: Action.SAVED_DASHBOARD_FILTER_CREATED,
      context: null,
      dashboard_id: dashboard.id,
      dashboard_group_id: dashboard.dashboard_group_id
    })

    tryCreateSavedDashboardFilter(
      {
        title,
        dashboard_group_id: dashboard.dashboard_group_id,
        conditions: formatPutConditions(dashboardFilter, dashboardFilterOptions)
      },
      (err, resource) => {
        if (!err && resource) {
          setSavedDashboardFilter(resource, true)
          setTab('saved')
        }
      }
    )
  }

  const handleUpdate = (id: string, title: string) => {
    if (isPublic) return

    UserEvents.log({
      action: Action.SAVED_DASHBOARD_FILTER_EDITED,
      context: Context.CONDITIONS,
      dashboard_id: dashboard.id,
      dashboard_group_id: dashboard.dashboard_group_id
    })

    tryUpdateSavedDashboardFilter(
      id,
      {
        title,
        conditions: formatPutConditions(dashboardFilter, dashboardFilterOptions)
      },
      (err, resource) => {
        if (!err && resource) {
          setSavedDashboardFilter(resource, true)
          setTab('saved')
        }
      }
    )
  }

  // success
  return (
    <>
      <Button
        disabled={dashboardFilter.length === 0}
        fullWidth={isMobile}
        onClick={() => {
          setDashboardFilter([])

          if (!isPublic) {
            UserEvents.log({
              action: Action.SELECTION_RESETTED,
              context: null,
              dashboard_id: dashboard.id,
              dashboard_group_id: dashboard.dashboard_group_id
            })
          }
        }}
        size={isMobile ? 'large' : undefined}
        variant={isMobile ? 'outlined' : 'text'}
      >
        {t('global', 'clear')}
      </Button>
      {isMobile ? null : (
        <SplitButton
          color="neutral"
          disabled={dashboardFilter.length === 0}
          onClick={() => setDialog('save')}
          options={[
            {
              onClick: () => setDialog('save'),
              title: t('global', 'save_as')
            },
            {
              disabled: savedDashboardFilters.length === 0,
              nestedOptions:
                savedDashboardFilters.length > 0
                  ? savedDashboardFilters.map((filter) => {
                    return {
                      onClick: () => handleUpdate(filter.id, filter.title),
                      title: filter.title
                    }
                  })
                  : undefined,
              nestedAnchor: 'left',
              onClick: () => {},
              title: t('global', 'update') + '...'
            }
          ]}
          title={t('global', 'save_as')}
          variant="contained"
        />
      )}

      {/*-- dialogs --*/}
      <SaveUserFilterDialog
        onClose={() => setDialog(false)}
        onSave={handleSave}
        open={dialog === 'save'}
        savedDashboardFilters={savedDashboardFilters}
      />
    </>
  )
}

export default Actions
